import { SHIFT_HOURS } from '../core/clock';
import { formatMoney, formatViews } from '../core/math';
import { DIFFICULTIES } from './config';
import { LEVEL_EMOJI, indexLevel, rankFor } from './scoring';
import type { GameResult } from './types';

/** Hour-by-hour Index as emoji squares — the Wordle-style heart of the share card. */
export function indexStrip(result: GameResult): string {
  const squares: string[] = [];
  for (let h = 0; h < SHIFT_HOURS; h++) {
    const value = result.stats.hourlyIndex[h];
    if (value === undefined) {
      squares.push('⬛');
    } else if (result.reason === 'closed' && h === lastHour(result)) {
      squares.push(LEVEL_EMOJI.red);
    } else {
      squares.push(LEVEL_EMOJI[indexLevel(value)]);
    }
  }
  return squares.join('');
}

function lastHour(result: GameResult): number {
  return Math.max(0, result.stats.hourlyIndex.length - 1);
}

export function shiftLabel(result: GameResult): string {
  if (result.mode === 'daily') return `Daily Shift #${result.shiftNumber}`;
  return `Free Play · ${DIFFICULTIES[result.difficulty].label}`;
}

/** A local-news headline written from the night's most remarkable stat. */
export function headlineFor(result: GameResult): string {
  const s = result.stats;
  if (result.reason === 'closed') {
    return 'WAFFLE HOUSE CLOSES OVERNIGHT; FEMA DEPLOYS TO SCENE, NATION IN SHOCK';
  }
  if (result.hostPleased) {
    return 'LOCAL WAFFLE HOUSE "HAS FOUND ITS NEW HOST," SAYS NO ONE, EXPLAINS NOTHING';
  }
  if (s.chairsCaught >= 2) {
    return `COOK CATCHES ${s.chairsCaught} THROWN CHAIRS, NEVER LOOKS UP FROM HASH BROWNS`;
  }
  if (result.femaVerdict === 'green' && result.stormName) {
    return `FEMA: WAFFLE HOUSE INDEX STAYED GREEN THROUGH HURRICANE ${result.stormName}`;
  }
  if (s.chairsCaught === 1) {
    return 'INTERNET CROWNS NEW SUPERHERO AFTER COOK SNAGS CHAIR MID-AIR';
  }
  if (s.unbothered >= 4) {
    return 'WITNESSES SAY COOK REMAINED "COMPLETELY UNBOTHERED" DURING 3AM BRAWL';
  }
  if (result.proposalYes) {
    return 'COUPLE GETS ENGAGED OVER PERFECTLY SCATTERED HASH BROWNS';
  }
  if (s.perfect >= 20) {
    return `AREA COOK SCATTERS, SMOTHERS AND COVERS ${s.perfect} ORDERS WITH "SURGICAL PRECISION"`;
  }
  if (s.walkouts >= 6) {
    return 'CUSTOMERS WALK OUT OF WAFFLE HOUSE; COOK BLAMES "THE CLUB LETTING OUT"';
  }
  return 'NIGHT SHIFT ENDS AT LOCAL WAFFLE HOUSE; DOORS STILL DO NOT LOCK';
}

export function shareText(result: GameResult, url: string): string {
  const rank = rankFor(result);
  const s = result.stats;
  const lines = [
    `3AM SHIFT 🧇 ${shiftLabel(result)}`,
    `Waffle House Index: ${indexStrip(result)}`,
    result.reason === 'closed' ? '🟥 CLOSED. FEMA HAS BEEN NOTIFIED.' : '✅ Survived until 6AM',
    `💵 ${formatMoney(result.tips)} tips · 🥔 ${s.hashBrowns} hash brown${s.hashBrowns === 1 ? '' : 's'}`,
  ];
  const extras: string[] = [];
  if (s.chairsThrown > 0) extras.push(`🪑 ${s.chairsCaught}/${s.chairsThrown} chairs caught`);
  if (s.unbothered > 0) extras.push(`😐 ${s.unbothered} unbothered`);
  if (s.views > 0) extras.push(`📱 ${formatViews(s.views)} views`);
  if (extras.length) lines.push(extras.join(' · '));
  if (result.calmedBefore !== null)
    lines.push(`🥊 Calmed ${result.calmedBefore}/3 at shift change`);
  lines.push(`🏅 ${rank.title}`);
  lines.push(url);
  return lines.join('\n');
}
